<template>
  <div class="tw-my-6 tw-flex tw-justify-between">
    <cl-form-input
      id="user_uuid"
      type="hidden"
      :value="uuid"
      test-id="profile-user-uuid"
    />
    <cl-form-input
      id="user_name"
      type="hidden"
      :value="name"
      test-id="profile-user-name"
    />
    <cl-form-input
      id="user_email"
      type="hidden"
      :value="email"
      test-id="profile-user-email"
    />
    <cl-form-input
      id="user_organisation"
      type="hidden"
      :value="organisation"
      test-id="profile-user-organisation"
    />
    <div>
      <cl-breadcrumbs
        :page-meta="pageMeta"
        :route-path="route.path"
        class="tw-mb-4"
        @on-click="goTo"
      />

      <div class="tw-flex tw-items-center tw-gap-4">
        <div
          v-if="pageMeta.icon"
          :data-test-id="`page-title-solution-icon-prefix`"
          class="tw-mb-3 tw-w-9 tw-h-9 tw-p-0.5 tw-m-0 tw-border-2 tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-place-self-center tw-border-dorian-gray-200"
        >
          <component :is="pageMeta.icon" />
        </div>
        <div class="tw-flex tw-gap-2">
          <cl-heading size="h3">
            {{ pageMeta.title }}
          </cl-heading>
          <t-page-header-info
            v-if="pageMeta.info"
            class="tw-self-start"
            :docs-link="pageMeta.info.docsLink"
            :text="pageMeta.info.text"
          />
        </div>
        <cl-pill
          class="tw-mb-3"
          v-if="pageMeta.pill"
          :variant="pageMeta.pill.variant"
        >
          <div class="tw-text-lg">
            {{ pageMeta.pill.text }}
          </div>
        </cl-pill>
      </div>
    </div>
    <cl-header-dropdown :data-test-id="dataTestId">
      <template #title>
        <p class="tw-mb-0 tw-font-bold">{{ name }}</p>
        <p class="tw-mb-0 tw-text-sm">{{ email }}</p>
      </template>
      <template #products>
        <cl-header-dropdown-product
          v-for="product in productLinks"
          :key="product.title"
          :label="product.active ? '' : $t('Try Now')"
          :link="product.link"
          :data-test-id="`header-menu-product-${product.title.toLowerCase()}`"
        >
          <div class="tw-flex tw-gap-4">
            <img
              class="tw-relative tw-h-7 tw-w-7"
              :src="product.icon"
              :alt="product.description"
            />
            <div>
              <div class="tw-text-primary-800">
                {{ product.title }}
              </div>
              <div class="tw-text-primary-800 tw-text-sm tw-font-bold">
                {{ product.description }}
              </div>
            </div>
          </div>
        </cl-header-dropdown-product>
      </template>
      <template #appearance>
        <div
          class="tw-relative tw-flex tw-items-center tw-justify-between"
          @click.stop
        >
          <span>Theme:</span>
          <cl-form-radio-group
            button
            :options="themeOptions"
            :selected-value="userPrefferedTheme"
            @update:selected="
              (selected: string) => setUserPreferredTheme(selected)
            "
          />
        </div>
      </template>
      <template #links>
        <cl-header-dropdown-link
          v-for="link in userLinks"
          :key="link.link"
          :link="link.link"
          :label="link.title"
          :target="link.target"
          :data-test-id="`header-menu-link-${link.title.toLowerCase()}`"
        >
          <component :is="link.icon" :width="'18'" :height="'18'" />
        </cl-header-dropdown-link>
        <router-link v-slot="{ href }" :to="{ name: 'logout' }">
          <cl-header-dropdown-link
            :link="href"
            :label="$t('Logout')"
            data-test-id="user-menu-logout"
          >
            <cl-icon-tabler-logout :width="'18'" :height="'18'" />
          </cl-header-dropdown-link>
        </router-link>
      </template>
    </cl-header-dropdown>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useTheme } from "@/composables";
import { THEME } from "@/constants";
import TPageHeaderInfo from "./TPageHeaderInfo.vue";
import { ProductLink, UserLink } from "./TPageHeader.types";
import { PageMeta } from "@/store/modules/pageMeta";

type Props = {
  userLinks: UserLink[];
  productLinks: ProductLink[];
  dataTestId: string;
};

defineProps<Props>();

const store = useStore();
const router = useRouter();
const route = useRoute();

const { userPrefferedTheme, setUserPreferredTheme } = useTheme();

const themeOptions = [
  { label: "Dark", value: THEME.DARK },
  { label: "Light", value: THEME.LIGHT },
  { label: "System", value: THEME.SYSTEM },
];

const pageMeta = computed<PageMeta>(() => store.getters["pageMeta/pageMeta"]);
const authUser = computed(() => store.getters["authentication/authUser"]);
const selectedAccount = computed(
  () => store.getters["authentication/selectedAccount"],
);
const selectedCustomer = computed(
  () => store.getters["authentication/selectedCustomer"],
);

const name = computed(() => authUser.value?.accountname);
const organisation = computed(() => authUser.value?.organisation);
const email = computed(() => authUser.value?.email);
const uuid = computed(() => authUser.value?.uuid);

const getRegex = (accountType: "System" | "MSP" | "Customer") => {
  switch (accountType) {
    case "System":
      return selectedCustomer.value ? /^\/system\/msp\// : /^\/system\//;
    case "MSP":
      return selectedCustomer.value ? /^\/msp\// : /^\/system\/msp\//;
    case "Customer":
      return selectedCustomer.value ? /^\/msp\// : null;
    default:
      return null;
  }
};

const goTo = (link: string) => {
  const regex = getRegex(selectedAccount.value.type_name);

  if (regex) {
    const updatedRegex = link.replace(regex, "/");
    router.push(updatedRegex).catch((err) => err);
  }
};
</script>
